/**
 * Клиент notifications-service для колокольчика трекера.
 *
 * Сервис живёт вне трекера, и его спека нет в генерации openapi (`npm run
 * generate:api` собирает клиентов только из swagger трекера и базы знаний),
 * поэтому четыре ручки описаны руками поверх общего axios — так же, как это
 * сделано в CRM и в кабинете агента.
 */
import { http } from '@/api/http'

import { NOTIFICATION_APP, type Notification, type NotificationsListResponse } from './types'

const BASE_PATH = '/api/v1/notifications'

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Терпимый маппер: сервис отдаёт camelCase, метаданные — строкой JSON, а по
 * SSE прилетает та же форма.
 */
export function mapNotification(raw: any): Notification {
  let metadata: Record<string, unknown> = {}

  if (raw?.metadata) {
    try {
      metadata = typeof raw.metadata === 'string' ? JSON.parse(raw.metadata) : raw.metadata
    } catch {
      metadata = {}
    }
  }

  return {
    id: raw.id,
    notificationType: raw.notificationType ?? raw.notification_type ?? '',
    title: raw.title ?? '',
    body: raw.body ?? '',
    metadata,
    channelsSent: raw.channelsSent ?? raw.channels_sent ?? [],
    isRead: raw.isRead ?? raw.is_read ?? false,
    createdAt: raw.createdAt ?? raw.created_at ?? '',
    readAt: raw.readAt ?? raw.read_at ?? null,
    app: raw.app ?? NOTIFICATION_APP,
    url: raw.url ?? '',
  }
}

export const notificationsApi = {
  async list(page = 1, pageSize = 20): Promise<NotificationsListResponse> {
    // Пагинация — вложенный PageRequest, uint64 приезжает строками (DEV-302).
    const { data } = await http.get(BASE_PATH, {
      params: { 'pagination.page': page, 'pagination.perPage': pageSize, app: NOTIFICATION_APP },
    })

    return {
      notifications: (data.notifications ?? []).map(mapNotification),
      total: Number.parseInt(data.pagination?.total ?? '0', 10) || 0,
      page: Number.parseInt(data.pagination?.page ?? '0', 10) || page,
      pageSize: Number.parseInt(data.pagination?.perPage ?? '0', 10) || pageSize,
    }
  },

  async markRead(notificationId: string): Promise<void> {
    // Приложение здесь не нужно: id глобально уникален, и сервис всё равно
    // сверяет владельца.
    await http.post(`${BASE_PATH}/${notificationId}/read`)
  },

  async markAllRead(): Promise<number> {
    // Без app кнопка в трекере погасила бы и бейдж CRM.
    const { data } = await http.post(`${BASE_PATH}/read-all`, { app: NOTIFICATION_APP })

    return data.markedCount ?? 0
  },

  async unreadCount(): Promise<number> {
    const { data } = await http.get(`${BASE_PATH}/unread-count`, {
      params: { app: NOTIFICATION_APP },
    })

    return data.count ?? 0
  },
}
